import {Box, Button} from '@viptech/react-components'
import {useCallback, useEffect, useReducer} from 'react'
import {Circle, Group, Layer, Line, Rect} from 'react-konva'
import {assignPolygonsByCross, getSidesPolygons} from 'src/common/components/camera-image-draw/RenderPolygonBackgroundUtil'
import {CamerasKonvaImageDetections} from 'src/common/components/cameras-konva-image-detections/CamerasKonvaImageDetections'
import {posTuple} from 'src/common/utils/Internal'
import {useCameraConfigContext} from 'src/pages/camera-config/context/CameraConfigContext'
import mkKey from 'src/utilities/mkKey'
import {areaHasMultiplePoints} from '../../../pages/camera-config/components/camera-barriers/components/ListBarriersOnCards'
import {imageDrawStyles} from '../../generics/components/image-draw/ImageDrawBase'
import {INITIAL_STATE, invScalePoints, pointsToPos, reducer, safePos} from '../../generics/components/image-draw/ImageDrawFunctions'
import {ImageDrawProps} from '../../generics/components/image-draw/ImageDrawProps'
import {KonvaImageDrawToolbar} from './components/konva-image-draw-toolbar/KonvaImageDrawToolbar'

export function CameraImageDraw(cameraImageDrawProps: ImageDrawProps) {
  const {konvaImageRef} = useCameraConfigContext()
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE)
  const {points, pos} = state
  const {dim, scale} = konvaImageRef.current || {scale: 0}

  const {
    src,
    onSave,
    onCancel,
    editable,
    points: _pointsInital,
    getPoints,
    cancel,
    save,
    isKonvaImageDrawToolbar,
    maxAmountOfPoints,
    auxiliaryClick,
    disableAuxiliaryClick,
    pointsToDrawWithSvg,
  } = cameraImageDrawProps

  useEffect(() => {
    if (!getPoints) return
    if (points.length >= 2 || editable === true) getPoints(points)
  }, [points, getPoints])

  const onClickCancel = useCallback(() => {
    if (!onCancel) return
    onCancel()
    dispatch({
      type: 'cancel',
      payload: pointsToPos(_pointsInital || [], scale),
    })
  }, [_pointsInital, onCancel, scale])

  const onClickSave = useCallback(() => {
    if (!onSave || points.length === 0 || scale === 0) return
    onSave(invScalePoints(points, scale))
    dispatch({type: 'save'})
  }, [onSave, points, scale])

  useEffect(() => {
    if (cancel === 'cancel') onClickCancel()
    else if (save === 'save') onClickSave()
  }, [cancel, save, onClickCancel, onClickSave])

  useEffect(() => {
    if (_pointsInital && scale !== 0) {
      dispatch({
        type: 'points-init',
        payload: pointsToPos(_pointsInital, scale),
      })
    }
  }, [_pointsInital, scale])

  const separatePointsByReference = useCallback(
    (points: {x: number; y: number; reference?: number}[]) => {
      return points.reduce(
        (acc, point) => {
          const ref = point.reference || 0
          if (!acc[ref]) acc[ref] = []
          acc[ref].push(point)
          return acc
        },
        {} as Record<number, typeof points>,
      )
    },
    [points],
  )

  function RenderParallelBackground() {
    const sides = getSidesPolygons(points, {width: dim!.width, height: dim!.height, x: 0, y: 0})
    if (!sides) return null

    const [p1, p2] = points
    const {blue} = assignPolygonsByCross(sides, p1, p2)

    return <Line points={blue.flatMap((p) => [p.x, p.y])} closed fill='rgba(0,0,255,0.25)' />
  }

  return (
    <Box display='flex' direction='column' rowGap='10px' align='center'>
      {src && (
        <>
          <CamerasKonvaImageDetections
            StageProps={{style: imageDrawStyles.stage}}
            src={src}
            konvaImageRef={konvaImageRef}
            pointsToDrawWithSvg={pointsToDrawWithSvg}>
            {dim && scale > 0 && (
              <Layer>
                <Rect
                  {...dim}
                  onClick={(e) => {
                    if (!editable) return
                    if (maxAmountOfPoints && points.length >= maxAmountOfPoints) return
                    const pos = e.target.getRelativePointerPosition()
                    if (!pos) return
                    dispatch({
                      type: 'point-new',
                      payload: {...pos, key: mkKey()},
                    })
                  }}
                />
                <Group name='points'>
                  {areaHasMultiplePoints(points) ? (
                    Object.entries(separatePointsByReference(points)).map(([ref, groupedPoints]) => (
                      <Line
                        key={`line-group-${ref}`}
                        points={groupedPoints.flatMap((it) => [it.x, it.y]).concat(groupedPoints.length === 0 ? [] : posTuple(groupedPoints[0]))}
                        stroke='blue'
                      />
                    ))
                  ) : (
                    <Line points={points.flatMap((it) => [it.x, it.y]).concat(points.length === 0 ? [] : posTuple(points[0]))} stroke='blue' />
                  )}

                  {maxAmountOfPoints === 2 && points.length === 2 && dim && <RenderParallelBackground />}

                  {editable &&
                    points.map((it, index) => {
                      const isDragging = pos?.key === it.key
                      const isLast = index === points.length - 1
                      return (
                        <Circle
                          key={`draw-circle-${it.x}-${it.y}-${index}`}
                          x={it.x}
                          y={it.y}
                          radius={9}
                          fill={isDragging ? 'red' : isLast ? 'yellow' : 'blue'}
                          draggable
                          onDragMove={(e) =>
                            dispatch({
                              type: 'pos-move',
                              payload: {
                                ...it,
                                ...safePos({x: e.target.attrs.x, y: e.target.attrs.y}, it, dim),
                              },
                            })
                          }
                          onDragStart={() => dispatch({type: 'pos-move', payload: it})}
                          onDragEnd={() => dispatch({type: 'pos-end'})}
                        />
                      )
                    })}
                </Group>
              </Layer>
            )}
          </CamerasKonvaImageDetections>

          {isKonvaImageDrawToolbar && (
            <Box display='flex' align='center' justify={auxiliaryClick ? 'space-between' : 'center'} width='100%'>
              <KonvaImageDrawToolbar
                editable={editable}
                onClickRedo={() => dispatch({type: 'redo'})}
                onClickUndo={() => dispatch({type: 'undo'})}
                onClickClearLast={() => dispatch({type: 'clear-last'})}
                onClickClear={() => dispatch({type: 'clear'})}
              />

              {auxiliaryClick && (
                <Button
                  disabled={disableAuxiliaryClick}
                  height='38px'
                  variant='outlined'
                  fontSize='12px'
                  disabledTextColor='#8E8E8E'
                  borderRadius='10px'
                  onClick={() => auxiliaryClick()}>
                  Salvar Linha
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
